import { useCallback, useRef } from "react";

/**
 * True when a mouse event landed on the backdrop element ITSELF and inside its
 * client area — not on a child, and not on the backdrop's own scrollbar.
 *
 * A backdrop that is also a scroll container (a dialog taller than the screen)
 * paints its scrollbar inside its border box, and a press on that scrollbar is
 * a mouse event whose target is the backdrop. `clientWidth` / `clientHeight`
 * exclude the scrollbars, so a point past them is on a scrollbar.
 */
export function hitsBackdropClientArea(e: React.MouseEvent<HTMLElement>): boolean {
  const el = e.currentTarget;
  if (e.target !== el) return false;
  const rect = el.getBoundingClientRect();
  return (
    e.clientX - rect.left - el.clientLeft < el.clientWidth &&
    e.clientY - rect.top - el.clientTop < el.clientHeight
  );
}

/**
 * PB-CDP-7 (audit r1, LOW-2): backdrop dismissal for a self-rolled scrim that
 * may scroll (`PDeleteConfirmation`, `PDeleteCompany`). Closes only when the
 * press AND the click both hit the backdrop's client area: a press on its
 * scrollbar, or a drag that started inside the dialog (selecting text) and
 * ended on the scrim, closes nothing.
 *
 * The press is recorded in the CAPTURE phase (audit r2, L1), so every press —
 * also one on a child that stops its propagation — overwrites the last: a
 * press on the scrim that produced no click (a right-click, a release outside
 * the window) cannot combine with a later drag out of the dialog.
 *
 * Spread `onMouseDownCapture` / `onClick` onto the backdrop; `closable` gates
 * the close (e.g. not while the dialog's request runs).
 */
export function useBackdropPressDismiss(
  onClose: (e: React.MouseEvent<HTMLElement>) => void,
  closable: boolean,
) {
  const pressedOnBackdrop = useRef(false);
  const onMouseDownCapture = useCallback((e: React.MouseEvent<HTMLElement>) => {
    pressedOnBackdrop.current = hitsBackdropClientArea(e);
  }, []);
  const onClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const pressed = pressedOnBackdrop.current;
      pressedOnBackdrop.current = false;
      if (pressed && closable && hitsBackdropClientArea(e)) onClose(e);
    },
    [closable, onClose],
  );
  return { onMouseDownCapture, onClick };
}
